import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Lock,
  AlertCircle,
  Loader2,
  ArrowRight,
  ShieldCheck,
} from 'lucide-react';

import { Card, CardContent } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { useRecruiterAuth } from '../../../CompanyAdmin/Context/RecruiterAuthContext';

export default function RecruiterCompleteProfile() {
  const { recruiter, isAuthenticated, completeProfile } = useRecruiterAuth();
  const navigate = useNavigate();

  const [companyName, setCompanyName] = useState(recruiter?.company_name || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) navigate('/recruiter/login');
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!companyName.trim()) {
      setError('Please enter your company name.');
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await completeProfile({
        company_name: companyName.trim(),
        new_password: newPassword,
      });
      navigate('/recruiter/dashboard');
    } catch (err) {
      setError(err.message || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 dark:from-slate-950 dark:via-slate-900 dark:to-blue-950/30">
      {/* Top logo */}
      <div className="px-6 py-4">
        <a href="/" className="inline-block">
          <img
            src="/Newzenalphablue.png"
            alt="SearchJob.ai"
            className="h-10 w-auto"
          />
        </a>
      </div>

      <div className="mx-auto flex max-w-5xl flex-col items-center justify-center px-4 py-8">
        <Card className="w-full overflow-hidden border border-border/40 shadow-lg">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px]">
            {/* Form */}
            <div className="p-8 lg:p-10">
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div>
                  <h1 className="text-xl font-bold tracking-tight">
                    Complete Your Recruiter Profile
                  </h1>
                  <p className="text-xs text-muted-foreground">
                    Finish setting up so you can start managing candidates.
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="companyName">Company Name</Label>
                  <div className="relative">
                    <Building2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="companyName"
                      type="text"
                      placeholder="Your agency / company name"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      className="pl-9"
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="newPassword"
                      type="password"
                      placeholder="At least 6 characters"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="pl-9"
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="Re-enter your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-9"
                      disabled={loading}
                    />
                  </div>
                </div>

                {error && (
                  <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 pt-2">
                  <Button type="submit" disabled={loading} className="flex-1 sm:flex-none">
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving…
                      </>
                    ) : (
                      <>
                        Save & Continue
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/recruiter/dashboard')}
                    disabled={loading}
                  >
                    Skip
                  </Button>
                </div>
              </form>

              <p className="mt-6 text-xs text-muted-foreground">
                By continuing, you agree to our{' '}
                <a href="/terms" className="font-medium text-foreground hover:underline">
                  Terms
                </a>{' '}
                and{' '}
                <a
                  href="/privacy"
                  className="font-medium text-foreground hover:underline"
                >
                  Privacy Policy
                </a>
                .
              </p>
            </div>

            {/* Side illustration */}
            <div className="hidden bg-gradient-to-br from-primary/10 via-primary/5 to-transparent lg:flex lg:items-center lg:justify-center lg:p-6">
              <img
                src="https://cdn.builder.io/api/v1/image/assets/TEMP/5e5cac7852424a1242a40a6c85ea3ed154da859a360e0fcb3f8eac46509d3baa?placeholderIfAbsent=true&apiKey=011132b2f41c4af7b38e22acc88aa803"
                alt="Recruiter illustration"
                className="h-auto w-full max-w-[260px]"
              />
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
